import * as React from 'react';
import { Fragment } from 'react';

/** Material UI Packages. */
import { FormGroup, FormControlLabel, Checkbox } from '@mui/material';

/** Internal Components. */
import InputField, { DateField, TimeField } from 'shared/ui/InputForm';
import {
    AutocompleteField_String,
    AutocompleteField,
    AutocompleteField_CodeName,
    MultiAutocompleteField,
} from 'shared/ui/AutoCompleteForm';
import AfternoonSelector from 'shared/ui/Checkbox';
import { handleRequiredState } from 'shared/lib/handleGoogle';
import GoogleLocation from 'shared/ui/GoogleLocation';
/** Renders the status form with multiple input fields including date, autocomplete, and conditional fields. */
const SINGLE_AUTOCOMPLETE_STYLE = {
    width: '400px',
    '&& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { py: '5px' },
};

export const renderStatusForm = ({
    status,
    statusConfig,
    hasAlias,
    cntrList,
    aliasList,
    statusOptions,
    userRole,
    handleEventDateChange,
    handleRelatedDataChange,
    handleAliasChange,
    handleStatusChange,
    handleStatusListChange,
    handleAllSelectedChange,
    renderAssociateFields,
    isCaseClosureComplete = false,
    isEdit,
}) => {
    let allSelected = false;
    let selectedIds = new Set();
    if (!isEdit) {
        const childIdList = Array.isArray(status.child_id) ? status.child_id : [];
        selectedIds = new Set(childIdList.map((c) => c.id));
        const selectable = cntrList.filter((item) => item.id !== 0).filter((item) => item.name !== '');
        allSelected =
            selectable.length > 0 &&
            selectable.length === selectedIds.size &&
            selectable.every((s) => selectedIds.has(s.id));
    }

    const transformContainerList = cntrList.map((item) => {
        return { id: item.id, content: item.name };
    });

    return (
        <>
            <DateField
                label={'Input Date'}
                onChange={(e) => handleEventDateChange(e.target.value, 'date')}
                value={status.event_date}
                name={'date'}
                disabled={isCaseClosureComplete}
                sx={{ mb: '10px', gap: 1 }}
                width={'400px'}
                labelStyle={{ fontSize: 12 }}
            />
            {isEdit ? (
                <AutocompleteField
                    label={statusConfig.type === 'oim' || statusConfig.type === 'oex' ? 'CNTR' : 'Alias'}
                    name={'container'}
                    value={status.child_id}
                    onChange={handleRelatedDataChange}
                    options={transformContainerList}
                    AutocompleteStyle={SINGLE_AUTOCOMPLETE_STYLE}
                    labelStyle={{ fontSize: 12 }}
                    sx={{ mb: '10px', gap: 1 }}
                    disable={!isCaseClosureComplete}
                    additional={{
                        disablePortal: false,
                    }}
                />
            ) : (
                <>
                    <MultiAutocompleteField
                        label={statusConfig.type === 'oim' || statusConfig.type === 'oex' ? 'CNTR' : 'Alias'}
                        name="container"
                        value={status.child_id}
                        options={cntrList}
                        onChange={(event, newValue) => handleRelatedDataChange(event, newValue)}
                        autocompleteStyle={{
                            width: '400px',
                            height: 'auto',
                            // Same 27px input box as SINGLE_AUTOCOMPLETE_STYLE (grows with chips).
                            '&& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': { py: '5px' },
                            '& .MuiInputBase-input': { height: '13px' },
                            '& .MuiAutocomplete-popupIndicator': { width: '23.1px' },
                        }}
                        labelStyle={{ fontSize: 12 }}
                        sx={{
                            mb: '5px',
                            gap: 1,
                            '& .MuiInputBase-input': {
                                fontSize: '13px',
                                height: '12px',
                            },
                        }}
                        disable={isCaseClosureComplete}
                        additional={{
                            disablePortal: false,
                        }}
                    />
                    <FormGroup>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    name="all_selected"
                                    checked={Boolean(allSelected)}
                                    onChange={(e) => handleAllSelectedChange(cntrList, e.target.checked)}
                                />
                            }
                            label="Select all containers/aliases."
                            sx={{
                                ml: 0,
                                mr: 0,
                                mb: '5px',
                                gap: '5px',
                                alignItems: 'center',
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    color: '#003a52',
                                },
                            }}
                        />
                    </FormGroup>
                </>
            )}
            {hasAlias && (
                <AutocompleteField
                    label={'Alias'}
                    name={'alias'}
                    value={status.alias_id}
                    onChange={handleAliasChange}
                    options={aliasList}
                    AutocompleteStyle={SINGLE_AUTOCOMPLETE_STYLE}
                    labelStyle={{ fontSize: 12 }}
                    sx={{ mb: '10px', gap: 1 }}
                    disable={!isCaseClosureComplete}
                />
            )}
            <AutocompleteField_String
                label={'Status'}
                name={'status'}
                value={status.status_message_id}
                onChange={handleStatusChange}
                onInputChange={handleStatusListChange}
                options={statusOptions}
                freeSolo={false}
                AutocompleteStyle={SINGLE_AUTOCOMPLETE_STYLE}
                sx={{ mb: '10px', gap: 1 }}
                labelStyle={{ fontSize: 12 }}
                disable={isCaseClosureComplete}
                useCustomComparisons={true}
                userRole={userRole}
            />
            {renderAssociateFields()}
        </>
    );
};

/**
 * Works out which input fields to show when an existing status entry is opened for editing.
 *
 * The entry row does not carry the message's associate name, so the fields are derived from
 * the values it holds. A stored time (or an afternoon choice) turns the date field into a
 * datetime field while every other field the entry has — location, textfield — stays visible,
 * so an Air "{Location} updated to {Datetime}" entry opens with all three inputs.
 */
export const deriveEditAssociateValue = (entry) => {
    const hasValue = (key) => entry?.[key] !== null && entry?.[key] !== undefined && entry?.[key] !== '';
    const hasTime = hasValue('associate_time') || (entry?.afternoon !== null && entry?.afternoon !== undefined);

    const parts = ['associate_location', 'associate_textfield'].filter(hasValue);
    if (hasTime) {
        parts.push('associate_datetime');
    } else if (hasValue('associate_date')) {
        parts.push('associate_date');
    }
    return parts;
};

/** Extracts date parts from a given string. */
export const extractDateParts = (associate) => {
    const parts = associate.split('_');
    return parts.slice(1);
};

/** Renders the appropriate field based on the associate value type.
 *  @param {string} value - The field type to render.
 */
export const renderSwitch = (
    value,
    status,
    handleDateChange,
    handleDateTimeChange,
    handleTextChange,
    handleAfternoonChange,
    setStatus,
    locationList,
    handleLocationChange,
    handleLocationListChange,
    tabValue,
    disableTime,
    isCaseClosureComplete = false,
) => {
    const result = extractDateParts(value);

    return result.map((item, index) => {
        switch (item) {
            case 'date':
                return (
                    <DateField
                        key={`date-${index}`}
                        label={'Date'}
                        onChange={(e) => handleDateChange(e.target.value, 'date')}
                        value={status.associate_date}
                        name={'date'}
                        disabled={isCaseClosureComplete}
                        sx={{ mb: '10px', gap: 1 }}
                        width={'400px'}
                        labelStyle={{ fontSize: 12 }}
                    />
                );
            case 'datetime':
                return (
                    <Fragment key={index}>
                        <DateField
                            label={'Date'}
                            onChange={(e) => handleDateChange(e.target.value, 'date')}
                            value={status.associate_date}
                            name={'date'}
                            disabled={isCaseClosureComplete}
                            sx={{ mb: '10px', gap: 1 }}
                            width={'400px'}
                            labelStyle={{ fontSize: 12 }}
                        />
                        <TimeField
                            label={'Time'}
                            onChange={(e) => handleDateTimeChange(e.target.value, 'date')}
                            value={status.associate_time}
                            name={'date'}
                            disabled={isCaseClosureComplete}
                            sx={{ mb: '10px', gap: 1 }}
                            width={'400px'}
                            labelStyle={{ fontSize: 12 }}
                            disableTime={disableTime}
                        />
                        <AfternoonSelector
                            status={status}
                            onChange={handleAfternoonChange}
                            disabled={isCaseClosureComplete}
                        />
                    </Fragment>
                );
            case 'textfield':
                return (
                    <InputField
                        key={`textfield-${index}`}
                        label={'Info'}
                        onChange={(e) => handleTextChange(e.target.value, 'text')}
                        value={status.associate_textfield}
                        disabled={isCaseClosureComplete}
                        sx={{ mb: '10px', gap: 1 }}
                        labelStyle={{ fontSize: 12, ...(tabValue === 4 && { width: '70px' }) }}
                        inputStyle={{
                            fontSize: '13px',
                            display: 'flex',
                            '& .MuiInputBase-input': {
                                p: '6px',
                                bgcolor: 'white',
                            },
                            width: '205px',
                        }}
                    />
                );
            case 'location':
                return tabValue === 4 ? (
                    <GoogleLocation
                        key={`location-${index}`}
                        keyname={'location'}
                        label={'Location'}
                        value={status.associate_location}
                        handleRequiredState={(newValue) =>
                            handleRequiredState(newValue, {
                                keyname: 'associate_location',
                                setState: setStatus,
                            })
                        }
                        disabled={isCaseClosureComplete}
                        sx={{ width: '280px' }}
                        typographyStyle={{ width: '70px' }}
                        boxStyle={{ gap: '10px', mb: '10px' }}
                    />
                ) : (
                    <AutocompleteField_CodeName
                        key={`autocomplete-${index}`}
                        label={'Location'}
                        sx={{ mb: '10px', gap: 1 }}
                        value={status.associate_location}
                        onChange={handleLocationChange}
                        onInputChange={handleLocationListChange}
                        name={'location'}
                        options={locationList || []}
                        AutocompleteStyle={SINGLE_AUTOCOMPLETE_STYLE}
                        labelStyle={{ fontSize: 12 }}
                        disable={true || isCaseClosureComplete}
                        displayKey={'name'}
                    />
                );
            default:
                return null;
        }
    });
};
